import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { lookupAdminSample } from "../../services/api";
import useIsMobile from "../../hooks/useIsMobile";

const s = {
  page: { maxWidth: 900, margin: "0 auto", padding: "32px 24px" },
  title: { fontFamily: "var(--font-heading)", fontSize: 30, fontWeight: 900, color: "var(--text-primary)", marginBottom: 8 },
  intro: { fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 18 },
  card: {
    background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8,
    padding: 18, boxShadow: "var(--dashboard-card-shadow)",
  },
  label: { display: "block", fontSize: 12, fontWeight: 900, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 },
  form: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  input: {
    flex: "1 1 280px", border: "1.5px solid var(--border)", borderRadius: "var(--dashboard-control-radius)",
    background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", padding: "11px 12px",
    fontSize: 15,
  },
  btn: {
    border: "none", borderRadius: "var(--dashboard-control-radius)", background: "var(--teal)",
    color: "#fff", padding: "11px 16px", fontSize: 13, fontWeight: 900, cursor: "pointer",
  },
  secondary: {
    border: "1px solid var(--border)", borderRadius: "var(--dashboard-control-radius)",
    background: "var(--dashboard-selector-bg)", color: "var(--text-primary)",
    padding: "10px 14px", fontSize: 13, fontWeight: 900, cursor: "pointer",
  },
  status: { marginTop: 12, fontSize: 13, fontWeight: 800 },
  result: { marginTop: 16, border: "1px solid var(--border)", borderRadius: 8, padding: 14, background: "var(--bg-page)" },
  resultTitle: { fontSize: 15, fontWeight: 900, color: "var(--text-primary)", marginBottom: 8 },
  meta: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 },
  metaLabel: { fontSize: 11, fontWeight: 900, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em" },
  metaValue: { fontSize: 14, color: "var(--text-primary)", fontWeight: 700, marginTop: 3 },
  modalBackdrop: { position: "fixed", inset: 0, zIndex: 1500, background: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 },
  modal: { width: "min(420px, 94vw)", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 24px 80px rgba(15, 23, 42, 0.35)", padding: 16 },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 },
};

export function SampleLookupPanel({ embedded = false, compact = false, resultInModal = false }) {
  const navigate = useNavigate();
  const [sampleId, setSampleId] = useState("");
  const [sample, setSample] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);

  async function submit(event) {
    event.preventDefault();
    const cleanId = sampleId.trim();
    if (!cleanId) {
      setStatus({ type: "err", text: "Enter a Sample ID to look up." });
      setSample(null);
      return;
    }

    setLoading(true);
    setStatus(null);
    setSample(null);
    try {
      const res = await lookupAdminSample(cleanId);
      const nextSample = res.data.sample;
      if (!nextSample?.customerEmail || !nextSample?.labSampleId) {
        setStatus({ type: "err", text: "That sample is missing customer routing information." });
        return;
      }
      setSample(nextSample);
      if (resultInModal) setShowResultModal(true);
      setStatus({ type: "ok", text: "Sample found. Open the dashboard view when ready." });
    } catch (err) {
      setStatus({ type: "err", text: err.response?.data?.error || "Sample lookup failed." });
    } finally {
      setLoading(false);
    }
  }

  function openDashboard() {
    if (!sample) return;
    const url = `/dashboard?adminEmail=${encodeURIComponent(sample.customerEmail)}&sampleId=${encodeURIComponent(sample.labSampleId)}`;
    if (resultInModal) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(url);
  }

  function closeResultModal() {
    setShowResultModal(false);
  }

  const formStyle = compact ? { ...s.form, alignItems: "end" } : s.form;
  const labelStyle = compact ? { flex: "1 1 150px", minWidth: 0 } : { flex: "1 1 280px" };
  const inputStyle = compact ? { ...s.input, width: "100%", boxSizing: "border-box" } : s.input;
  const buttonStyle = compact ? { ...s.btn, alignSelf: "end", flex: "0 0 auto" } : { ...s.btn, alignSelf: "end" };

  const content = (
    <>
      {!embedded && (
        <>
          <h1 style={s.title}>Sample ID Lookup</h1>
          <div style={s.intro}>Search by lab sample ID to open an admin review dashboard for the related customer, vehicle, and report.</div>
        </>
      )}
        <form style={formStyle} onSubmit={submit}>
          <label style={labelStyle}>
            <span style={s.label}>Sample ID</span>
            <input
              style={inputStyle}
              value={sampleId}
              onChange={(event) => setSampleId(event.target.value)}
              placeholder="Enter Sample ID"
            />
          </label>
          <button type="submit" style={buttonStyle} disabled={loading}>
            {loading ? "Looking up..." : "Search"}
          </button>
        </form>

        {status && <div style={{ ...s.status, color: status.type === "ok" ? "#16a34a" : "#dc2626" }}>{status.text}</div>}

        {sample && !resultInModal && <SampleLookupResult sample={sample} onOpenDashboard={openDashboard} />}
        {sample && resultInModal && showResultModal && (
          <div style={s.modalBackdrop} onMouseDown={closeResultModal}>
            <section style={s.modal} onMouseDown={(event) => event.stopPropagation()}>
              <div style={s.modalHeader}>
                <div>
                  <div style={s.label}>Sample ID Lookup</div>
                  <div style={s.resultTitle}>{sample.labSampleId}</div>
                </div>
                <button type="button" style={s.secondary} onClick={closeResultModal}>Close</button>
              </div>
              <SampleLookupResult sample={sample} onOpenDashboard={openDashboard} compact />
            </section>
          </div>
        )}
    </>
  );

  if (embedded) return content;

  return (
    <section style={s.card}>
      {content}
    </section>
  );
}

function SampleLookupResult({ sample, onOpenDashboard, compact = false }) {
  return (
    <div style={{ ...s.result, marginTop: compact ? 0 : s.result.marginTop }}>
      {!compact && <div style={s.resultTitle}>{sample.labSampleId}</div>}
      <div style={s.meta}>
        <Meta label="Customer" value={sample.customerName || "N/A"} />
        <Meta label="Email" value={sample.customerEmail} />
        <Meta label="Vehicle" value={sample.vehicle || "N/A"} />
        <Meta label="Component" value={sample.componentType || "N/A"} />
        <Meta label="Status" value={sample.isReady ? "Ready" : "Pending"} />
      </div>
      <button type="button" style={{ ...s.btn, width: compact ? "100%" : undefined }} onClick={onOpenDashboard}>Open Dashboard View</button>
    </div>
  );
}

export default function SampleLookupPage() {
  const isMobile = useIsMobile();

  return (
    <div style={{ ...s.page, padding: isMobile ? "18px 12px" : s.page.padding }}>
      <SampleLookupPanel />
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div style={s.metaLabel}>{label}</div>
      <div style={s.metaValue}>{value}</div>
    </div>
  );
}
